import type { NextPageContext } from 'next';
import { createTRPCNext } from '@trpc/next';
import { httpBatchLink, httpLink, loggerLink, splitLink, TRPCClientError } from '@trpc/client';
import { QueryCache, MutationCache } from '@tanstack/react-query';
import superjson from 'superjson';
import { toast } from 'react-toastify';

import type { AppRouter } from '~/server/routers/_app';

import ErrorToastBody from '~/components/error-handling/ErrorToastBody';
import { browserClerkSessionRecovery, clerkSessionRecoveryLink } from '~/utils/clerkSessionRecovery';
import type { CreateReactUtilsProxy } from '@trpc/react-query/shared';

export const transformer = superjson;

export interface SSRContext extends NextPageContext {
	/**
	 * Set HTTP Status code
	 * @usage
	 * const utils = trpc.useContext();
	 * if (utils.ssrContext) {
	 *   utils.ssrContext.status = 404;
	 * }
	 */
	status?: number;
}

/** Procedure paths served by /api/trpc-pdf. See the splitLink below for why. */
export const PDF_PROCEDURE_PATHS = new Set(['proposal.renderPdf', 'proposal.generateSendPdf']);

function getBaseUrl() {
	if (typeof window !== 'undefined') {
		return '';
	}
	// reference for vercel.com
	if (process.env.VERCEL_URL) {
		return `https://${process.env.VERCEL_URL}`;
	}

	// assume localhost
	return `http://localhost:${process.env.PORT ?? 3000}`;
}

function isTRPCClientError(error: unknown): error is TRPCClientError<AppRouter> {
	return error instanceof TRPCClientError;
}

export function shouldRetryQuery(failureCount: number, error: unknown) {
	if (isTRPCClientError(error) && error.data?.httpStatus === 404) return false;

	return failureCount < 3;
}

/**
 * Extend `NextPageContext` with meta data that can be picked up by `responseMeta()` when server-side rendering
 */

const queryCache = new QueryCache({
	onError: (error, query) => {
		// `ErrorToastBody` shows a stack trace, so customer-facing pages opt out and render their own error UI.
		if (query.meta?.skipErrorToast === true) {
			return;
		}

		toast.error(ErrorToastBody({ error }), {
			toastId: undefined,
			position: 'top-right',
			autoClose: false,
			hideProgressBar: false,
			closeOnClick: false,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		});
	},
});

const mutationCache = new MutationCache({
	onError: (error, _variables, _context, mutation) => {
		// Same opt-out as the query cache above.
		if (mutation.meta?.skipErrorToast === true) {
			return;
		}

		// @ts-ignore If error.data doesn't exist, no harm
		if (error?.data?.code === 'CONFLICT') {
			console.warn('Not showing an error toast for a CONFLICT error', error);
			return;
		}
		toast.error(ErrorToastBody({ error }), {
			toastId: undefined,
			position: 'top-right',
			autoClose: false,
			hideProgressBar: false,
			closeOnClick: false,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		});
	},
});

export const trpc = createTRPCNext<AppRouter, SSRContext>({
	overrides: {
		useMutation: {
			/**
			 * This function is called whenever a `.useMutation` succeeds
			 * From https://trpc.io/docs/v10/client/react/useUtils#invalidate-full-cache-on-every-mutation
			 **/
			async onSuccess(opts) {
				/**
				 * @note that order here matters:
				 * The order here allows route changes in `onSuccess` without
				 * having a flash of content change whilst redirecting.
				 **/
				// Calls the `onSuccess` defined in the `useQuery()`-options:
				await opts.originalFn();

				if (opts.meta?.skipQueryInvalidation) {
					return;
				}

				// Invalidate all queries in the react-query cache:
				await opts.queryClient.invalidateQueries();
			},
		},
	},
	config() {
		/**
		 * If you want to use SSR, you need to use the server's full URL
		 * @link https://trpc.io/docs/ssr
		 */
		return {
			/**
			 * @link https://trpc.io/docs/links
			 */
			links: [
				// adds pretty logs to your console in development and logs errors in production
				loggerLink({
					enabled: (opts) => process.env.NODE_ENV === 'development' || (opts.direction === 'down' && opts.result instanceof Error),
				}),
				clerkSessionRecoveryLink(browserClerkSessionRecovery),
				/**
				 * This allows us to skip batched request on a by query basis. Docs below.
				 * @link https://trpc.io/docs/client/links/splitLink#disable-batching-for-certain-requests
				 * adding a {trpc: context: {skipBatch: true}} to the query context will skip batching for that query.
				 */
				/**
				 * Procedures that render a PDF go to their own endpoint.
				 *
				 * Not a behavioral choice: @sparticuz/chromium is ~66MB, and Next traces it into any
				 * route that can reach it, so serving these from /api/trpc put a headless browser in
				 * the bundle carrying every procedure in the app — 269MB against Vercel's 250MB
				 * limit. Both endpoints serve the same router, so this only changes which bundle the
				 * request lands in. Keep this list in sync with the exclude in next.config.ts.
				 */
				splitLink({
					condition: (op) => PDF_PROCEDURE_PATHS.has(op.path),
					true: httpLink({
						url: `${getBaseUrl()}/api/trpc-pdf`,
					}),
					false: splitLink({
						condition(op) {
							// check for context property `skipBatch`
							return op.context.skipBatch === true;
						},
						// when condition is true, use normal request
						true: httpLink({
							url: `${getBaseUrl()}/api/trpc`,
						}),
						// when condition is false, use batching
						false: httpBatchLink({
							url: `${getBaseUrl()}/api/trpc`,
							/**
							 * Set custom request headers on every request from tRPC
							 * @link https://trpc.io/docs/ssr
							 */
							// headers() {
							// 	if (!ctx?.req?.headers) {
							// 		return {};
							// 	}
							// 	// To use SSR properly, you need to forward the client's headers to the server
							// 	// This is so you can pass through things like cookies when we're server-side rendering

							// 	const {
							// 		// If you're using Node 18 before 18.15.0, omit the "connection" header
							// 		connection: _connection,
							// 		...headers
							// 	} = ctx.req.headers;
							// 	return headers;
							// },
						}),
					}),
				}),
			],
			/**
			 * @link https://trpc.io/docs/data-transformers
			 */
			transformer,
			/**
			 * @link https://tanstack.com/query/v4/docs/react/reference/QueryClient
			 *
			 * For documentation of query options:
			 * @link https://tanstack.com/query/v4/docs/react/reference/useQuery
			 */
			queryClientConfig: {
				queryCache: queryCache,
				mutationCache: mutationCache,
				defaultOptions: {
					mutations: {
						networkMode: 'always',
					},
					queries: {
						networkMode: 'always',
						retry: shouldRetryQuery,
						//Don't refresh when the window gets focus back
						refetchOnWindowFocus: false,
						//Don't refresh when a bad connection is restored
						refetchOnReconnect: false,
					},
				},
			},
		};
	},
	/**
	 * @link https://trpc.io/docs/ssr
	 */
	ssr: true,
	/**
	 * Set headers or status code when doing SSR
	 */
	responseMeta(opts) {
		const ctx = opts.ctx as SSRContext;

		if (ctx.status) {
			// If HTTP status set, propagate that
			return {
				status: ctx.status,
			};
		}

		const error = opts.clientErrors[0];
		if (error) {
			// Propagate http first error from API calls
			return {
				status: error.data?.httpStatus ?? 500,
			};
		}

		// for app caching with SSR see https://trpc.io/docs/caching

		return {};
	},
});

export type TRPCUtils = CreateReactUtilsProxy<AppRouter, SSRContext>['client'];
